import React, { useState, useEffect, useContext } from "react";
import { Grid, Divider, withStyles } from "@material-ui/core";
import {
  searchLocalRecords,
  searchMdmRecords,
  searchDnbRecords,
  createNewEntity,
} from "../../API/Entity";
import { scoreValues } from "../../API/config";
import ModalDialogContext from "../../Context/ModalDialog/ModalDialogContext";
import { getAttributeValue } from "../../Utilities/Functions";
import { SearchPanel, SearchResult } from "./Component";
import CreateEntity from "./Component/CreateEntity";
import styles from "./styles";
import { useTranslation } from "react-i18next";

const SearchRecords = (props) => {
  const {
    classes,
    recordActions,
    dropTopMargin,
    onNewRecordSubmit,
    hideCheckTypeSelection,
    disableCheckTypeSelection,
    defaultCheckType,
    user,
    hasPermission,
    searchQuery,
    selectedRecord,
  } = props;

  const { showMessage, showErrorMessage } = useContext(ModalDialogContext);
  const { t } = useTranslation();
  const [searchLocalEntities, setSearchLocalEntities] = useState(false);
  const [searchMdmEntities, setSearchMdmEntities] = useState(false);
  const [searchDnbEntities, setSearchDnbEntities] = useState(false);
  const [creatingNewRecord, setCreatingNewRecord] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState({});
  const [localEntities, setLocalEntities] = useState([]);
  const [localCount, setLocalCount] = useState(0);
  const [mdmEntities, setMdmEntities] = useState([]);
  const [mdmCount, setMdmCount] = useState(0);
  const [dnbEntities, setDnbEntities] = useState([]);
  const [dnbCount, setDnbCount] = useState(0);
  const [localPageNumber, setLocalpageNumber] = useState(1);
  const [mdmPageNumber, setMdmPageNumber] = useState(1);
  const [dnbPageNumber, setDnbPageNumber] = useState(1);
  const [mdmRowsPerPage, setMdmRowsPerPage] = useState(30);
  const [dnbRowsPerPage, setDnbRowsPerPage] = useState(30);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(30);

  useEffect(() => {
    const localEntitiesSearch = async () => {
      try {
        const results = await searchLocalRecords({
          searchCriteria,
          pageNumber: localPageNumber,
          rowsPerPage: localRowsPerPage,
        });
        const { data } = results;
        const { records, totals } = data || {};
        const { totalCount = 0 } = totals;
        setSearchLocalEntities(false);
        setLocalCount(totalCount);
        setLocalEntities(records.map(convertLocalRecord));
      } catch (error) {
        showErrorMessage({ error });
        setSearchLocalEntities(false);
      }
    };

    if (searchLocalEntities) {
      localEntitiesSearch();
    }
  }, [searchLocalEntities]);

  useEffect(() => {
    const mdmEntitiesSearch = async () => {
      try {
        const results = await searchMdmRecords({
          searchCriteria,
          pageNumber: mdmPageNumber,
          rowsPerPage: mdmRowsPerPage,
        });
        const { data } = results;
        const { records, totalCount } = data || {};
        setSearchMdmEntities(false);
        setMdmCount(totalCount);
        setMdmEntities(records.map(convertMdmRecord));
      } catch (error) {
        showErrorMessage({ error });
        setSearchMdmEntities(false);
      }
    };

    if (searchMdmEntities) {
      mdmEntitiesSearch();
    }
  }, [searchMdmEntities]);

  useEffect(() => {
    const dnbEntitiesSearch = async () => {
      try {
        const results = await searchDnbRecords({
          searchCriteria,
          pageNumber: dnbPageNumber,
          rowsPerPage: dnbRowsPerPage,
        });
        const { data } = results;
        const { records, totalCount } = data || {};
        setSearchDnbEntities(false);
        setDnbCount(totalCount);
        setDnbEntities(records.map(convertDnbRecord));
      } catch (error) {
        showErrorMessage({ error });
        setSearchDnbEntities(false);
      }
    };

    if (searchDnbEntities) {
      dnbEntitiesSearch();
    }
  }, [searchDnbEntities]);

  const convertLocalRecord = (record) => {
    const {
      id,
      entity_category: entityCategory,
      entity_subcategory: entitySubcategory,
      attributes = [],
      scores = [],
      country,
      city,
      checkRequests,
    } = record;

    const nameAttr = attributes.find((a) => a.id === "3") || {};

    const dunsAttr = attributes.find((a) => a.id === "2") || {};

    const kycScore = scores.find((s) => s.check_type === "KYC") || {};
    const amlScore = scores.find((s) => s.check_type === "AML") || {};

    const dunsNumber = getAttributeValue(dunsAttr);

    return {
      id,
      name: getAttributeValue(nameAttr),
      country,
      entityCategory,
      entitySubcategory,
      dunsNumber: dunsNumber === "[No DUNS number found]" ? "-" : dunsNumber,
      kycScore:
        kycScore.accepted_score ||
        kycScore.guided_score ||
        scoreValues.cannotScore,
      kycValidFrom: kycScore.valid_from_date || "-",
      kycValidTo: kycScore.expiry_date || "-",
      amlScore:
        amlScore.accepted_score ||
        amlScore.guided_score ||
        scoreValues.cannotScore,
      amlValidFrom: amlScore.valid_from_date || "-",
      amlValidTo: amlScore.expiry_date || "-",
      canWorkWith: kycScore.canWorkWith || amlScore.canWorkWith || "-",
      kycExpired: kycScore.expired,
      amlExpired: amlScore.expired,
      city: city,
      scores,
      checkRequests,
    };
  };

  const convertMdmRecord = (record) => {
    const { checkRequests = [], scores = [], address = {} } = record;
    const kycScore = scores.find((s) => s.check_type === "KYC") || {};
    const amlScore = scores.find((s) => s.check_type === "AML") || {};
    const city = address.city ? address.city : "-";
    return {
      id: record["JLL unique ID"],
      name: record["Full name"],
      country: record["Country of operations"],
      city: city,
      type: "",
      dunsNumber:
        record["DUNS number"] === "[No DUNS number found]"
          ? "-"
          : record["DUNS number"],
      kycScore:
        kycScore.accepted_score ||
        kycScore.guided_score ||
        scoreValues.cannotScore,
      kycValidFrom: kycScore.valid_from_date || "-",
      kycValidTo: kycScore.expiry_date || "-",
      amlScore:
        amlScore.accepted_score ||
        amlScore.guided_score ||
        scoreValues.cannotScore,
      amlValidFrom: amlScore.valid_from_date || "-",
      amlValidTo: amlScore.expiry_date || "-",
      canWorkWith: kycScore.canWorkWith || amlScore.canWorkWith || "-",
      kycExpired: kycScore.expired,
      amlExpired: amlScore.expired,
      record,
      checkRequests,
      scores,
    };
  };

  const convertDnbRecord = (record) => {
    const {
      checkRequests = [],
      scores = [],
      address = {},
      dunsId = 0,
    } = record;
    const {
      addressLine1 = "-",
      city = "-",
      stateProvince = "-",
      countryIso3,
    } = address;
    return {
      id: record["JLL unique ID"],
      name: record["Full name"],
      type: "",
      dunsNumber: dunsId,
      record,
      checkRequests,
      scores,
      addressLine1,
      city,
      stateProvince,
      countryIso3,
    };
  };

  const onSearch = async (searchFields) => {
    setMdmPageNumber(1);
    setLocalpageNumber(1);
    setSearchCriteria(searchFields);
    setLocalEntities([]);
    setLocalCount(0);
    setMdmEntities([]);
    setMdmCount(0);
    setDnbEntities([]);
    setDnbCount(0);
    setSearchLocalEntities(true);
    setSearchMdmEntities(true);
  };

  const onDnbSearch = async (searchFields) => {
    setSearchCriteria(searchFields);
    setLocalEntities([]);
    setLocalCount(0);
    setMdmEntities([]);
    setMdmCount(0);
    setDnbEntities([]);
    setDnbCount(0);
    setSearchDnbEntities(true);
  };

  const onNewRecord = async () => {
    setCreatingNewRecord(true);
  };

  const onNewRecordClose = async () => {
    setCreatingNewRecord(false);
  };

  const handlePageChange = async (e, tab) => {
    if (tab === "mdm") {
      await setMdmPageNumber(e);
      setSearchMdmEntities(true);
    } else if (tab === "dnb") {
      await setDnbPageNumber(e);
      setSearchDnbEntities(true);
    } else {
      await setLocalpageNumber(e);
      setSearchLocalEntities(true);
    }
  };

  const handleRowsPerPageChange = async (e, tab) => {
    if (tab === "mdm") {
      await setMdmRowsPerPage(e);
      setSearchMdmEntities(true);
    } else if (tab === "dnb") {
      await setDnbRowsPerPage(e);
      setSearchDnbEntities(true);
    } else {
      await setLocalRowsPerPage(e);
      setSearchLocalEntities(true);
    }
  };

  const onCreateNewRecord = async (record) => {
    if (onNewRecordSubmit) {
      await onNewRecordSubmit(record, "New");
    } else {
      try {
        await createNewEntity(record);
        showMessage({ message: t("messages.record.recordCreated") });
      } catch (error) {
        showErrorMessage({ error });
      }
    }
  };

  const paddingTop = dropTopMargin ? 0 : 50;
  return (
    <Grid container>
      <Grid container className={classes.bodyContainer}>
        {/* Commenting out now, may be used later
          
         <Grid
          item
          xs={1}
          sm={4}
          md={2}
          lg={2}
          className={classes.sidebarContainer}
          style={{ paddingTop: paddingTop }}
        >
          <FilterSideBar />
        </Grid> */}
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <Grid container style={{ paddingTop: paddingTop }}>
            <Grid item xs={12}>
              <SearchPanel
                onSearch={onSearch}
                onDnbSearch={onDnbSearch}
                searching={
                  searchLocalEntities || searchMdmEntities || searchDnbEntities
                }
                searchQuery={searchQuery}
                onNewRecord={onNewRecord}
              />
              {creatingNewRecord && (
                <CreateEntity
                  open={creatingNewRecord}
                  onSubmit={onCreateNewRecord}
                  onClose={onNewRecordClose}
                  hideCheckTypeSelection={hideCheckTypeSelection}
                  disableCheckTypeSelection={disableCheckTypeSelection}
                  defaultCheckType={defaultCheckType}
                  user={user}
                  hasPermission={hasPermission}
                  selectedRecord={selectedRecord}
                />
              )}
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <SearchResult
                localEntitiesLoading={searchLocalEntities}
                mdmEntitiesLoading={searchMdmEntities}
                dnbEntitiesLoading={searchDnbEntities}
                localEntities={localEntities}
                mdmEntities={mdmEntities}
                dnbEntities={dnbEntities}
                recordActions={recordActions}
                mdmCount={mdmCount}
                localCount={localCount}
                dnbCount={dnbCount}
                localPageNumber={localPageNumber}
                mdmPageNumber={mdmPageNumber}
                dnbPageNumber={dnbPageNumber}
                localRowsPerPage={localRowsPerPage}
                mdmRowsPerPage={mdmRowsPerPage}
                dnbRowsPerPage={dnbRowsPerPage}
                handlePageChange={handlePageChange}
                handleRowsPerPageChange={handleRowsPerPageChange}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles, { withTheme: true })(SearchRecords);
